<script lang="ts">
  import { navigate } from 'svelte-routing';
  import {
    CONTINENT_BY_SLUG,
    slugifyCountryName,
    type RegionScope,
  } from '@atlasguess/games';
  import type { Country } from '@atlasguess/engine';

  interface Props {
    /**
     * Mode URL slug ({entity}-{region}, e.g. `cities-world`). The picker
     * navigates to `/play/{scopeSlug}/{format}` (no filter) for "All" or
     * `/play/{scopeSlug}/{format}/{filter}` otherwise.
     */
    scopeSlug: string;
    /** Mode format slug (`practice`, `quiz`, `daily`). */
    format: string;
    /**
     * Currently active filter slug (URL's 4th segment) — used to set the
     * native `<select>`'s value so the dropdown reflects the active state.
     * Empty / undefined means "All".
     */
    activeSlug: string | undefined;
    /**
     * Countries to surface in the per-country `<optgroup>`. For the city
     * picker this is the unique countries present in the city pool;
     * for the country picker callers pass an empty array since the
     * per-country option is currently not exposed there (continents only).
     * Already filtered + sorted by the caller — this component renders
     * verbatim.
     */
    countries: readonly Country[];
    /**
     * Region scope of the host mode. We hide the continent group when
     * the mode is already region-locked (e.g. a continent quiz) since
     * the player's continent is fixed by the mode itself.
     */
    region: RegionScope;
  }

  let { scopeSlug, format, activeSlug, countries, region }: Props = $props();

  // Hide continents for region-locked modes — they'd be no-ops at best,
  // misleading at worst (selecting "Asia" from a Europe-quiz dropdown
  // would empty the pool).
  const showContinents = $derived(region === 'world');

  /**
   * Continent options. Stable order matches sidebar continent quiz order
   * so the picker reads the same way across the app.
   */
  const CONTINENT_OPTIONS = Object.entries(CONTINENT_BY_SLUG)
    .sort(([, a], [, b]) => a.localeCompare(b))
    .map(([slug, name]) => ({ slug, name }));

  function urlFor(filterSlug: string): string {
    return filterSlug
      ? `/play/${scopeSlug}/${format}/${filterSlug}`
      : `/play/${scopeSlug}/${format}`;
  }

  function onChange(event: Event): void {
    const value = (event.currentTarget as HTMLSelectElement).value;
    navigate(urlFor(value));
  }
</script>

<!-- Native <select> with <optgroup> — same pattern as the prototype, no
     custom popover. Native control gives us free a11y, keyboard nav, and
     mobile picker UI. The "All" option doubles as the implicit label
     when no filter is active, so a separate "FILTER" lead-in is dropped
     to keep the corner chrome compact. -->
<div class="filter-picker">
  <select
    class="filter-picker__select"
    aria-label="Filter pool"
    value={activeSlug ?? ''}
    onchange={onChange}
  >
    <option value="">All Locations</option>
    {#if showContinents}
      <optgroup label="Continents">
        {#each CONTINENT_OPTIONS as opt (opt.slug)}
          <option value={opt.slug}>{opt.name}</option>
        {/each}
      </optgroup>
    {/if}
    {#if countries.length > 0}
      <optgroup label="Countries">
        {#each countries as country (country.iso_a2)}
          <option value={slugifyCountryName(country.name)}>
            {country.name}
          </option>
        {/each}
      </optgroup>
    {/if}
  </select>
</div>
